import { toast } from 'react-toastify';
import { onError } from '@apollo/client/link/error';
import { GraphQLFormattedError } from 'graphql';
import { translate } from '@/i18n/utils';

type ErrorType = 'NETWORK' | 'GRAPHQL' | 'AUTHENTICATION' | 'AUTHORIZATION' | 'VALIDATION' | 'UNKNOWN';

type ErrorRule = {
  type: ErrorType;
  shouldShowToast: boolean;
  toastType: 'error' | 'warning' | 'info';
  getMessage?: (error: any) => string;
};

/** Правила для обработки ошибок */
const ERROR_RULES: ErrorRule[] = [
  {
    type: 'AUTHENTICATION',
    shouldShowToast: true,
    toastType: 'error',
    getMessage: () => translate('Authentication failed. Please log in again.'),
  },
  {
    type: 'AUTHORIZATION',
    shouldShowToast: true,
    toastType: 'error',
    getMessage: () => translate('You do not have permission to perform this action.'),
  },
  {
    type: 'NETWORK',
    shouldShowToast: true,
    toastType: 'error',
    getMessage: () => translate('Network connection problem. Please check your internet connection.'),
  },
  {
    type: 'VALIDATION',
    shouldShowToast: true,
    toastType: 'error',
    getMessage: (error) => {
      const errorMessage = error.extensions.errorChain;
      if (errorMessage.includes('insufficient funds')) {
        return translate('Insufficient funds on balance. Please top up your account.');
      }
      if (errorMessage.includes('failed to validate RPL 2025 game: should have at least one RPL 2025 card')) {
        // on english
        return translate('You should have at least one RPL 2025 card');
      } else if (errorMessage.includes('failed to submit user team: only newcomers allowed')) {
        // on english
        return translate('Only newcomers are allowed to submit team');
      }
      if (errorMessage.includes('failed to validate team: too many card swaps')) {
        return translate('You can only swap up to 2 players when modifying your team');
      }

      // Обработка ошибки "same one exists" при изменении подписки
      if (errorMessage.includes('same one exists')) {
        return translate('Subscription change is already scheduled. Cancel the current change first.');
      }

      // Обработка ошибки превышения лимита игроков из одной команды
      const sameTeamRegex =
        /.*?(failed to submit user team with more players on the same team than is required) \(.*?SameTeamName:(.*?)\)/;
      const sameTeamMatch = errorMessage.match(sameTeamRegex);
      if (sameTeamMatch) {
        const teamName = sameTeamMatch[2].trim();
        // Используем существующий ключ локализации с добавлением названия команды
        return translate('team.too-much-players-from-same-team-with-name', { teamName });
      }

      return translate('Validation error. Please try again');
    },
  },
  {
    type: 'GRAPHQL',
    shouldShowToast: true,
    toastType: 'error',
    getMessage: (error) => error.message || translate('An error occurred while executing the request.'),
  },
  {
    type: 'UNKNOWN',
    shouldShowToast: true,
    toastType: 'error',
    getMessage: () => translate('An unknown error occurred. Please try again.'),
  },
];

/** Определение типа ошибки */
const getErrorType = (error: GraphQLFormattedError): ErrorType => {
  // Ошибки аутентификации
  if (error.extensions?.code === 'UNAUTHENTICATED' || error.message?.includes('authentication')) {
    return 'AUTHENTICATION';
  }

  // Ошибки авторизации
  if (
    error.extensions?.code === 'FORBIDDEN' ||
    error.message?.includes('authorization') ||
    error.message?.includes('forbidden')
  ) {
    return 'AUTHORIZATION';
  }

  // Ошибки валидации
  if (error.extensions?.type === 'ValidationFailed') {
    return 'VALIDATION';
  }

  // GraphQL ошибки
  if (error.extensions || error.locations) {
    return 'GRAPHQL';
  }

  return 'UNKNOWN';
};

/** Обработка ошибки согласно правилам */
const handleError = (errorType: ErrorType, originalError: GraphQLFormattedError) => {
  const rule = ERROR_RULES.find((r) => r.type === errorType);

  if (!rule || !rule.shouldShowToast) {
    return;
  }

  const message = rule.getMessage ? rule.getMessage(originalError) : originalError.message;

  switch (rule.toastType) {
    case 'error':
      toast.error(message);
      break;
    case 'warning':
      toast.warning(message);
      break;
    case 'info':
      toast.info(message);
      break;
  }
};

/** Исключения - операции, для которых не нужно показывать глобальные ошибки */
const SILENT_OPERATIONS = [
  'GetAppViewer', // Проверка аутентификации при загрузке
  // Добавьте сюда названия операций, для которых не нужны глобальные тосты
];

/** Создание error link для Apollo Client */
export const errorLink = onError(({ graphQLErrors, operation }) => {
  const operationName = operation.operationName;

  // Пропускаем "тихие" операции
  if (operationName && SILENT_OPERATIONS.includes(operationName)) {
    return;
  }

  // Обработка GraphQL ошибок
  if (graphQLErrors) {
    graphQLErrors.forEach((error) => {
      const errorType = getErrorType(error);

      console.group(`🚨 GraphQL Error [${errorType}]`);
      console.error('Operation:', operationName);
      console.error('Error:', error);
      console.groupEnd();

      handleError(errorType, error);
      return;
    });
  }
});

/** Утилита для временного отключения тостов для конкретной операции */
export const withSilentErrors = (operationName: string) => {
  if (!SILENT_OPERATIONS.includes(operationName)) {
    SILENT_OPERATIONS.push(operationName);
  }

  // Возвращаем функцию для восстановления
  return () => {
    const index = SILENT_OPERATIONS.indexOf(operationName);
    if (index > -1) {
      SILENT_OPERATIONS.splice(index, 1);
    }
  };
};

export { ERROR_RULES, SILENT_OPERATIONS };
